import {
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { NavItem, SideBarSection, TreeNode } from './types';
import { useTabManager } from '../../tab-manager/tab-manager';
import { NewDatasetAction } from '../../dataset/actions/new-dataset.action';
import { SidebarSection } from './common';
import { iconForGraphColumn } from '../../data-grid/utils';
import { buildDatasetTree, treeNodeToNavItem } from './tree-builder';
import { DeleteDatasetAction } from '../../dataset/actions/delete-dataset.action';
import { NewColumnAction } from '../../dataset/actions/new-column.action';
import { RenameDatasetAction } from '../../dataset/actions/rename-dataset-operation';
import { Assert, using } from '@cotera/utilities';
import { DisposableTargetedModelContext } from '../../actions/etc';
import { DatasetModel } from '../../dataset/model/dataset.model';
import { v4 } from 'uuid';
import { useAnalytics } from '@cotera/client/app/etc';
import { DeleteColumnAction } from '../../columns/common-actions/delete-column.action';
import { RenameColumnAction } from '../../columns/common-actions/rename-column.action';
import { makeColumnModel } from '../../lib/make-column-model';
import { DuplicateAction } from '../../graph/duplicate.action';
import { useToolSchemaLookup } from '@cotera/client/app/hooks/use-tool-schema';

export const Datasets = () => {
  const model = useInScopeModel(GraphModel)!;
  const analytics = useAnalytics();
  const toolSchemaLookup = useToolSchemaLookup();
  const tabManager = useTabManager();
  const graph = useWatchableValue(model.graph);
  const newDatasetAction = useAction(NewDatasetAction);
  const deleteDatasetAction = useAction(DeleteDatasetAction);
  const newColumnAction = useAction(NewColumnAction);
  const renameDatasetAction = useAction(RenameDatasetAction);
  const context = useActionsContext();
  const deleteColumnAction = useAction(DeleteColumnAction);
  const renameColumnAction = useAction(RenameColumnAction);
  const duplicateAction = useAction(DuplicateAction);

  const actions = {
    newColumnAction: {
      ...newColumnAction,
      filter: (item: TreeNode) => item.type === 'dataset',
      variant: 'grouped' as const,
      execute: async (item: TreeNode) => {
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            DatasetModel.fromGraph(model, item.name, {
              targeted: true,
              id: `${v4()}-${item.id}`,
            })
          ),
          async (_) => {
            await newColumnAction.execute({});
          }
        );
        return res;
      },
    },
    renameDatasetAction: {
      ...renameDatasetAction,
      filter: (item: TreeNode) => item.type === 'dataset',
      variant: 'grouped' as const, // Put in kebab menu for datasets
      execute: async (item: TreeNode) => {
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            DatasetModel.fromGraph(model, item.name, {
              targeted: true,
              id: `${v4()}-${item.id}`,
            })
          ),
          async (_) => {
            await renameDatasetAction.execute({});
          }
        );
        return res;
      },
    },
    deleteDatasetAction: {
      ...deleteDatasetAction,
      filter: (item: TreeNode) => item.type === 'dataset',
      variant: 'grouped' as const,
      execute: async (item: TreeNode) => {
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            DatasetModel.fromGraph(model, item.name, {
              targeted: true,
              id: `${v4()}-${item.id}`,
            })
          ),
          async (_) => {
            await deleteDatasetAction.execute({});
          }
        );
        return res;
      },
    },
    duplicateAction: {
      ...duplicateAction,
      filter: (item: TreeNode) => item.type === 'dataset',
      variant: 'grouped' as const,
      execute: async (item: TreeNode) => {
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            DatasetModel.fromGraph(model, item.name, {
              targeted: true,
              id: `${v4()}-${item.id}`,
            })
          ),
          async (_) => {
            await duplicateAction.execute();
          }
        );
        return res;
      },
    },
    renameColumnAction: {
      ...renameColumnAction,
      filter: (item: TreeNode) => item.type === 'column',
      variant: 'grouped' as const,
      execute: async (item: TreeNode) => {
        Assert.assert(item.type === 'column');
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            makeColumnModel(model, item, toolSchemaLookup)
          ),
          async (_) => {
            await renameColumnAction.execute({});
          }
        );
        return res;
      },
    },
    deleteColumnAction: {
      ...deleteColumnAction,
      filter: (item: TreeNode) => item.type === 'column',
      variant: 'grouped' as const,
      execute: async (item: TreeNode) => {
        Assert.assert(item.type === 'column');
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            makeColumnModel(model, item, toolSchemaLookup)
          ),
          async (_) => {
            await deleteColumnAction.execute({});
          }
        );
        return res;
      },
    },
    duplicateColumnAction: {
      ...duplicateAction,
      filter: (item: TreeNode) => item.type === 'column',
      variant: 'grouped' as const,
      execute: async (item: TreeNode) => {
        Assert.assert(item.type === 'column');
        const res = await using(
          new DisposableTargetedModelContext(
            context,
            makeColumnModel(model, item, toolSchemaLookup)
          ),
          async (_) => {
            await duplicateAction.execute();
          }
        );
        return res;
      },
    },
  };

  // Build the dependency-based tree structure
  const treeStructure = buildDatasetTree(graph);

  // Convert tree nodes to NavItems for rendering
  const topLevelItems: NavItem[] = treeStructure.topLevelDatasets.map(
    (datasetNode) =>
      treeNodeToNavItem(
        datasetNode,
        tabManager,
        graph,
        iconForGraphColumn,
        0,
        actions,
        analytics
      )
  );

  const datasetsSection: SideBarSection = {
    title: 'Datasets',
    items: topLevelItems,
    quickActions: [
      {
        icon: 'plus',
        label: 'New Dataset',
        onClick: async () => {
          await newDatasetAction.execute();
        },
      },
    ],
  };

  return <SidebarSection section={datasetsSection} />;
};
